// ============================================================================
// docker-images/.env — pure parsing and merging (no I/O)
//
// docker compose reads `.env` from its PROJECT directory, which is the
// directory of the first `-f` file: `docker-images/` for the generated
// `docker-images/docker-compose.yml`. The file holds the docker stack's secrets
// (DataProtection certificate, JWT signing secret) and is gitignored.
//
// Merging is idempotent and conservative: a key that already has a non-empty
// value is KEPT unless the caller forces it, every other line (the client's
// own variables, comments, blank lines) is preserved verbatim, and the file's
// end-of-line style is kept.
// ============================================================================

export const DOCKER_ENV_REL_PATH = 'docker-images/.env';

export const ENV_DP_PFX = 'DATAPROTECTION_PFX_BASE64';
export const ENV_DP_PASSWORD = 'DATAPROTECTION_PFX_PASSWORD';
export const ENV_DP_PREVIOUS_PFX = 'DATAPROTECTION_PREVIOUS_PFX_BASE64';
export const ENV_DP_PREVIOUS_PASSWORD = 'DATAPROTECTION_PREVIOUS_PFX_PASSWORD';
export const ENV_JWT_SECRET = 'JWT_SECRET';

/** Comment line carrying the certificate's public metadata (read back by `ss doctor`). */
export const DP_CERT_INFO_PREFIX = '# DataProtection certificate:';

const LINE_RE = /^\s*(?:export\s+)?([A-Za-z_][A-Za-z0-9_]*)\s*=\s*(.*)$/;

/** Unquote a dotenv value: 'literal', "double", or bare (trailing ` #comment` dropped). */
function unquote(raw: string): string {
  const value = raw.trim();
  if (value.length >= 2 && (value[0] === "'" || value[0] === '"')) {
    const quote = value[0];
    const end = value.indexOf(quote, 1);
    if (end > 0) return value.slice(1, end);
  }
  const comment = value.search(/\s#/);
  return (comment >= 0 ? value.slice(0, comment) : value).trim();
}

/** Parse a dotenv file into a key → value map (last occurrence wins, like compose). */
export function parseDotEnv(content: string): Map<string, string> {
  const values = new Map<string, string>();
  for (const line of (content ?? '').split(/\r?\n/)) {
    if (/^\s*#/.test(line)) continue;
    const match = LINE_RE.exec(line);
    if (match) values.set(match[1], unquote(match[2]));
  }
  return values;
}

/** True when `key` is present with a non-blank value. */
export function hasDotEnvValue(content: string, key: string): boolean {
  return (parseDotEnv(content).get(key) ?? '').trim().length > 0;
}

/**
 * Format one assignment. Single quotes make compose (and python-dotenv, used by
 * podman-compose) take the value literally — no `$` interpolation, no `#`
 * comment ambiguity.
 */
export function formatDotEnvLine(key: string, value: string): string {
  if (/['\r\n]/.test(value)) {
    throw new Error(`${key}: a value written to .env cannot contain a single quote or a line break`);
  }
  return `${key}='${value}'`;
}

export interface DotEnvMergeResult {
  content: string;
  /** Keys whose value was written (appended, filled in, or forced). */
  written: string[];
  /** Keys left untouched because they already had a value. */
  kept: string[];
}

/**
 * Merge `entries` into a dotenv file's content.
 *
 * - absent key            → appended (after `header`, when given, if anything is appended)
 * - present, blank value  → filled in place
 * - present, set value    → kept, unless `force`
 */
export function mergeDotEnv(
  content: string,
  entries: ReadonlyArray<readonly [string, string]>,
  options: { force?: boolean; header?: readonly string[] } = {},
): DotEnvMergeResult {
  const base = content ?? '';
  const eol = base.includes('\r\n') ? '\r\n' : '\n';
  const lines = base.length === 0 ? [] : base.split(/\r?\n/);
  // A trailing newline yields a last empty element — drop it, re-added on join.
  if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();

  const written: string[] = [];
  const kept: string[] = [];
  const appended: string[] = [];

  for (const [key, value] of entries) {
    let index = -1;
    for (let i = lines.length - 1; i >= 0; i--) {
      if (/^\s*#/.test(lines[i])) continue;
      const match = LINE_RE.exec(lines[i]);
      if (match && match[1] === key) { index = i; break; }
    }

    if (index >= 0) {
      const current = unquote(LINE_RE.exec(lines[index])![2]);
      if (current.trim().length > 0 && !options.force) {
        kept.push(key);
        continue;
      }
      lines[index] = formatDotEnvLine(key, value);
      written.push(key);
    } else {
      appended.push(formatDotEnvLine(key, value));
      written.push(key);
    }
  }

  if (appended.length > 0) {
    if (lines.length > 0 && lines[lines.length - 1].trim() !== '') lines.push('');
    lines.push(...(options.header ?? []), ...appended);
  }

  return { content: lines.length === 0 ? '' : lines.join(eol) + eol, written, kept };
}

/**
 * Replace (or insert) the certificate metadata comment. Public data only:
 * subject, thumbprint and expiry let `ss doctor` warn before the certificate
 * expires without ever reading the PFX.
 */
export function setCertificateInfoComment(
  content: string,
  info: { subject: string; thumbprint: string; notAfter: string },
): string {
  const eol = content.includes('\r\n') ? '\r\n' : '\n';
  const comment = `${DP_CERT_INFO_PREFIX} subject="${info.subject.replace(/"/g, "'")}" thumbprint=${info.thumbprint} notAfter=${info.notAfter}`;
  const lines = content.split(/\r?\n/);
  const index = lines.findIndex((l) => l.startsWith(DP_CERT_INFO_PREFIX));
  if (index >= 0) {
    lines[index] = comment;
    return lines.join(eol);
  }
  const pfxIndex = lines.findIndex((l) => LINE_RE.exec(l)?.[1] === ENV_DP_PFX);
  if (pfxIndex >= 0) {
    lines.splice(pfxIndex, 0, comment);
    return lines.join(eol);
  }
  return content;
}

/** Read the metadata comment back (null when absent or malformed). */
export function readCertificateInfoComment(content: string): { thumbprint: string; notAfter: string } | null {
  const line = (content ?? '').split(/\r?\n/).find((l) => l.startsWith(DP_CERT_INFO_PREFIX));
  if (!line) return null;
  const thumbprint = /thumbprint=([0-9A-Fa-f]+)/.exec(line)?.[1];
  const notAfter = /notAfter=(\S+)/.exec(line)?.[1];
  if (!thumbprint || !notAfter || Number.isNaN(Date.parse(notAfter))) return null;
  return { thumbprint, notAfter };
}
